import { useMutation } from '@tanstack/react-query';
import { ApiError } from '@/api/user-portal-api-generated';
import { SessionService } from '@/api/user-portal-service';
import { handleUnauthorized, isTenantNotFoundError } from '@/common/auth-utils';
import { HttpStatus } from '@/enums/http-status.enums';
import { useNavigation } from '@/hooks/use-navigation';
import { useRecentApps } from '@/hooks/use-recent-apps';
import { useToast } from '@/hooks/use-toast';
import type { UserApplication } from '@/types/api.types';
import { buildAppLaunchPath } from '@/utils/app-launch-utils';
import { logError } from '@/utils/error-logger';

class SessionExpiredError extends Error {}

interface UseApplicationLaunchOptions {
  onSuccess?: () => void;
}

interface LaunchVariables {
  application: UserApplication;
  targetWindow: Window | null;
}

interface UseApplicationLaunchResult {
  loadingAppId: string | null;
  launchApplication: (application: UserApplication) => void;
}

export function useApplicationLaunch(
  options?: UseApplicationLaunchOptions
): UseApplicationLaunchResult {
  const { addRecentApp } = useRecentApps();
  const toast = useToast();
  const { navigateToTenantSelection } = useNavigation();
  const mutation = useMutation({
    mutationFn: async ({ application, targetWindow }: LaunchVariables) => {
      const authed = await SessionService.authenticated();

      if (!authed) {
        throw new SessionExpiredError('Unauthorized');
      }

      return { application, targetWindow };
    },
    onSuccess: ({ application, targetWindow }) => {
      const targetUrl = buildAppLaunchPath(application);
      let launched = false;

      if (targetWindow && !targetWindow.closed) {
        targetWindow.opener = null;
        targetWindow.location.href = targetUrl;
        launched = true;
      } else {
        launched =
          window.open(targetUrl, '_blank', 'noopener,noreferrer') !== null;
      }

      if (!launched) {
        logError(
          new Error(`Popup blocked launching ${application.app_id}`),
          'applications:launch:popupBlocked'
        );
        toast.error({
          title: 'Launch blocked',
          message:
            'Your browser blocked the app window. Allow pop-ups for this site and try again.',
        });

        return;
      }

      addRecentApp(application.app_id);
      options?.onSuccess?.();
    },
    onError: (error, variables) => {
      variables.targetWindow?.close();

      if (error instanceof SessionExpiredError) {
        handleUnauthorized();

        return;
      }

      if (isTenantNotFoundError(error)) {
        navigateToTenantSelection();

        return;
      }

      if (
        error instanceof ApiError &&
        error.status === HttpStatus.UNAUTHORIZED
      ) {
        return;
      }

      logError(error, 'applications:launch');
      toast.error({
        title: 'Launch failed',
        message: "Couldn't open the app. Please try again.",
      });
    },
  });

  const launchApplication = (application: UserApplication) => {
    if (mutation.isPending) {
      return;
    }

    const targetWindow = window.open('about:blank', '_blank');
    mutation.mutate({ application, targetWindow });
  };

  const loadingAppId = mutation.isPending
    ? (mutation.variables?.application.app_id ?? null)
    : null;

  return {
    loadingAppId,
    launchApplication,
  };
}
